<template>
  <Modal :open="isOpen" size="medium" @close="closeModal">
    <h1 class="text-gray-900 font-bold mb-4">Modifier le produit</h1>

    <VeeForm v-slot="{ handleSubmit }" :validation-schema="schema" as="div">
      <form class="space-y-4" @submit="handleSubmit($event, handleUpdateProductItem)" ref="UpdateItemForm">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
          <div>
            <label for="itemTitle" class="block text-sm text-gray-700">
              Nom du produit
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2">
              <Field
                id="itemTitle"
                name="itemTitle"
                type="text"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Nom du produit"
                v-model="itemTitle"
              />
              <ErrorMessage name="itemTitle" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
          <div>
            <label for="itemPrice" class="block text-sm text-gray-700">
              Prix du produit
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2 relative">
              <Field
                id="itemPrice"
                name="itemPrice"
                type="text"
                class="pr-16 shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="249,99"
                v-model="itemPriceFormatted"
              />
              <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span class="text-gray-500 sm:text-sm" id="price-currency">EUR</span>
              </div>
              <ErrorMessage name="itemPrice" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
          <div>
            <label for="itemType" class="block text-sm text-gray-700">
              Type du produit
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2">
              <Field
                as="select"
                id="itemType"
                name="itemType"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                v-model="itemType"
              >
                <option disabled value="">Veuillez sélectionner un type</option>
                <option v-for="type in itemTypes(plan)" :key="type" :value="type">{{ $t(type) }}</option>
              </Field>
              <ErrorMessage name="itemType" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
          <div>
            <label for="itemReferenceId" class="block text-sm text-gray-700">
              Numéro de série ou IMEI
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2">
              <Field
                id="itemReferenceId"
                name="itemReferenceId"
                type="text"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="35478405412998702"
                v-model="itemReferenceId"
              />
              <ErrorMessage name="itemReferenceId" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
          <div>
            <label for="itemVendor" class="block text-sm text-gray-700">
              Vendeur / Marque
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2">
              <Field
                id="itemVendor"
                name="itemVendor"
                type="text"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Ex : Samsung, Apple, Sony..."
                v-model="itemVendor"
              />
              <ErrorMessage name="itemVendor" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
          <div>
            <label for="itemModel" class="block text-sm text-gray-700">
              Modèle
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2">
              <Field
                id="itemModel"
                name="itemModel"
                type="text"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Ex : Series 6, Q80T, Hero9, PlayStation 5..."
                v-model="itemModel"
              />
              <ErrorMessage name="itemModel" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
          <div>
            <label for="itemPurchaseDate" class="block text-sm text-gray-700">
              Date d'achat
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2 relative">
              <Field
                type="date"
                name="itemPurchaseDate"
                id="itemPurchaseDate"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                v-model="itemPurchaseDate"
              />
              <ErrorMessage name="itemPurchaseDate" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
        </div>

        <div v-if="productItem?.photoUrls?.length" class="mt-6" data-test="current-photos">
          <p class="block text-sm text-gray-700">{{ t("current_photos") }}</p>
          <div class="mt-2 flex flex-wrap gap-2">
            <img
              v-for="url in productItem.photoUrls"
              :key="url"
              :src="url"
              :alt="productItem.title"
              class="h-20 w-20 rounded-md object-cover border border-gray-200"
            />
          </div>
        </div>

        <ProductItemPhotosField
          :form-data="photosFormData"
          :min-photos="MIN_PHOTOS"
          :error="photosError"
          optional
          @files="handlePhotosChange"
        />

        <ProductPhotosCheckResult
          :checking="checking"
          :result="checkResult || storedPhotosResult"
          :hide-help="!checkResult"
        />

        <div class="mt-6">
          <div class="flex flex-row gap-4">
            <div class="sm:w-1/2">
              <button
                class="w-full border border-transparent px-4 py-2 text-lg font-medium transition duration-300 transform hover:scale-105 rounded-lg focus:outline-hidden text-button-font-primary bg-primary uppercase disabled:opacity-50 disabled:transform-none"
                type="submit"
                :disabled="checking || submitting"
                data-test="submit-product-item"
              >
                {{ photosNonCompliant ? t("register_anyway") : $t("Modifier") }}
              </button>
            </div>
            <div class="sm:w-1/2">
              <button
                class="w-full border border-transparent bg-red-100 px-4 py-2 text-lg font-medium text-red-700 transition duration-300 transform hover:scale-105 rounded-lg focus:outline-hidden"
                type="button"
                @click="closeModal"
              >
                {{ $t("Close") }}
              </button>
            </div>
          </div>
        </div>
      </form>
    </VeeForm>
  </Modal>
</template>

<script>
import { Modal } from "@estaly/ui";
import { mapActions, mapGetters } from "vuex";
import { useI18n } from "vue-i18n";
import { ErrorMessage, Field, Form as VeeForm } from "vee-validate";
import * as yup from "yup";
import ProductItemPhotosField from "@/views/plan/ProductItemPhotosField.vue";
import ProductPhotosCheckResult from "@/views/plan/ProductPhotosCheckResult.vue";

// Mirrors the backend floor: fewer photos cannot cover every side of a product.
const MIN_PHOTOS = 3;

export default {
  components: {
    Modal,
    ErrorMessage,
    Field,
    VeeForm,
    ProductItemPhotosField,
    ProductPhotosCheckResult,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  props: ["isOpen", "plan", "productItem"],

  data: () => {
    const schema = yup.object({
      itemTitle: yup.string().required("Le nom du produit est requis"),
      itemReferenceId: yup.string().required("Le numéro de série est requis"),
      itemVendor: yup.string().required("La marque du produit est requise"),
      itemModel: yup.string().required("Le modèle du produit est requis"),
      itemPurchaseDate: yup
        .date()
        .nullable()
        .transform((value, originalValue) => (originalValue === "" ? null : value))
        .max(new Date(), "La date d'achat ne peut pas être dans le futur")
        .min(
          new Date(new Date().setFullYear(new Date().getFullYear() - 3)),
          "La date d'achat ne peut pas être supérieure à 3 ans",
        )
        .required("La date d'achat est requise")
        .typeError("La date d'achat est invalide"),
      itemPrice: yup
        .string()
        .required("Le prix du produit est requis.")
        .matches(/^\d+(\.\d{1,2})?$|^\d+(,\d{1,2})?$/, "Le format du prix est invalide."),
      itemType: yup.string().required("Le type du produit est requis"),
    });
    return {
      MIN_PHOTOS,
      schema,
      updateSuccess: false,
      itemTitle: "",
      itemPrice: "",
      itemType: "",
      itemReferenceId: "",
      itemVendor: "",
      itemModel: "",
      itemPurchaseDate: "",
      // FileField mutates this FormData in place under the `item_images[]` key.
      photosFormData: new FormData(),
      photoCount: 0,
      photosError: null,
      checking: false,
      submitting: false,
      checkResult: null,
      // Bumped on every invalidation: a check answering after one is stale and ignored.
      photosCheckVersion: 0,
    };
  },

  computed: {
    photosNonCompliant() {
      return this.checkResult?.verdict === "FAIL";
    },
    // The warning raised when these photos were saved: gone as soon as new ones are picked.
    storedPhotosResult() {
      if (this.photoCount > 0 || this.productItem?.photosVerdict !== "FAIL") return null;

      return {
        verdict: "FAIL",
        missingFaces: this.productItem.photosMissingFaces,
        reasons: this.productItem.photosReasons,
      };
    },
    ...mapGetters("planSales", ["itemTypes"]),
    itemPriceFormatted: {
      get() {
        return this.itemPrice === undefined ? "" : this.itemPrice.toString().replace(".", ",");
      },
      set(value) {
        this.itemPrice = value.replace(",", ".");
      },
    },
  },

  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("plan", ["checkProductItemPhotos", "updateProductItem"]),

    closeModal() {
      this.$emit("closeUpdateProductItemModal", this.updateSuccess);
      this.resetPhotos();
    },

    // The text fields are repopulated by the productItem watcher, but the photos are not:
    // without this, reopening the modal would carry over the previous product's photos.
    resetPhotos() {
      this.photosFormData = new FormData();
      this.photoCount = 0;
      this.photosError = null;
      this.checking = false;
      this.invalidatePhotosCheck();
    },

    refreshPlanSale() {
      this.$emit("refreshPlanSale");
    },

    invalidatePhotosCheck() {
      this.photosCheckVersion += 1;
      this.checkResult = null;
    },

    // New photos invalidate the previous verdict: the next submission checks them again.
    handlePhotosChange(count) {
      this.photoCount = count;
      this.invalidatePhotosCheck();
    },

    // Photos are optional here, but a partial set is not: replacing them means replacing
    // all of them, and the new set goes through the same check and warning as at registration.
    // Guarded synchronously: a double click must not submit the product twice.
    async handleUpdateProductItem() {
      if (this.submitting) return;

      this.submitting = true;
      try {
        await this.submitProductItem();
      } finally {
        this.submitting = false;
      }
    },

    async submitProductItem() {
      if (this.photoCount > 0 && this.photoCount < MIN_PHOTOS) {
        this.photosError = this.t("min_photos_error");
        return;
      }
      this.photosError = null;

      let check = null;
      if (this.photoCount > 0) {
        const warningAcknowledged = this.photosNonCompliant;
        check = warningAcknowledged ? this.checkResult : await this.runPhotosCheck();
        if (!check?.photosVerdictToken) return;
        if (check.verdict === "FAIL" && !warningAcknowledged) return;
      }

      const success = await this.updateProductItem({
        planSaleId: this.plan.id,
        productItemId: this.productItem.id,
        params: {
          title: this.itemTitle,
          price: this.itemPrice,
          itemType: this.itemType,
          referenceId: this.itemReferenceId,
          vendor: this.itemVendor,
          model: this.itemModel,
          purchaseDate: this.itemPurchaseDate,
          ...(check ? { itemImageSignedIds: check.signedIds, photosVerdictToken: check.photosVerdictToken } : {}),
        },
      });
      if (!success) return;

      this.notify({
        category: "simple",
        type: "success",
        title: "Le produit a été modifié",
      });
      this.updateSuccess = true;
      this.closeModal();
      this.refreshPlanSale();
    },

    // Returns null when the facts, the photos or the modal changed while the check ran: its
    // token was minted for values that are no longer the ones about to be submitted.
    async runPhotosCheck() {
      const version = this.photosCheckVersion;
      this.checking = true;
      this.checkResult = null;

      this.photosFormData.set("title", this.itemTitle);
      this.photosFormData.set("vendor", this.itemVendor);
      this.photosFormData.set("model", this.itemModel);
      this.photosFormData.set("item_type", this.itemType);

      try {
        const result = await this.checkProductItemPhotos({
          planSaleId: this.plan.id,
          formData: this.photosFormData,
        });
        if (version !== this.photosCheckVersion) return null;

        this.checkResult = result;
        return result;
      } finally {
        this.checking = false;
      }
    },
  },

  watch: {
    // The verdict was computed against these facts: changing one requires a new check.
    itemTitle: "invalidatePhotosCheck",
    itemVendor: "invalidatePhotosCheck",
    itemModel: "invalidatePhotosCheck",
    itemType: "invalidatePhotosCheck",
    productItem: {
      deep: true,
      handler(newValue) {
        if (newValue) {
          this.itemTitle = newValue.title || "";
          this.itemPrice = newValue.price.toString().replace(".", ",") || "";
          this.itemType = newValue.itemType || "";
          this.itemReferenceId = newValue.referenceId || "";
          this.itemVendor = newValue.vendor || "";
          this.itemModel = newValue.model || "";
          if (newValue.purchaseDate) {
            const parts = newValue.purchaseDate.split("/");
            if (parts.length === 3) {
              this.itemPurchaseDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
            } else {
              this.itemPurchaseDate = "";
            }
          } else {
            this.itemPurchaseDate = "";
          }
        }
      },
      immediate: true,
    },
  },
};
</script>

<i18n src="./UpdateProductItemModal.i18n.json"></i18n>
